/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import Media from 'react-media';

import { Divider, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import useUserAgent from 'bundles/page/hooks/useUserAgent';
import { MAX_NUMBER_OF_KIM_ITEMS } from 'bundles/unified-description-page-common/constants';

const styles = {
  wrapper: () => css`
    ${breakpoints.up('md')} {
      border: 1px solid var(--cds-color-grey-50);
      border-radius: var(--cds-spacing-100);
      margin: -82px auto 0;
      background-color: var(--cds-color-white-0);
      box-shadow: 0 var(--cds-spacing-100) var(--cds-spacing-200) 0 rgb(0 0 0 / 16%);
      align-items: center;
      display: flex;
      justify-content: space-evenly;
      z-index: 1;
      position: relative;
    }

    ${breakpoints.down('sm')} {
      padding-top: var(--cds-spacing-400);
    }
  `,
  items: () => css`
    ${breakpoints.up('md')} {
      display: flex;
      flex: 1 1 0px;
      align-items: center;
      padding: var(--cds-spacing-600) 0;
      justify-content: center;
    }

    ${breakpoints.down('xs')} {
      padding: 0;
    }

    .cds-ContextualHelp-label p {
      color: var(--cds-color-grey-600);
    }
  `,
  desktopItem: (_: Theme, width?: string) => css`
    max-width: 35%;
    width: ${width ?? 'auto'};
    padding: 0 var(--cds-spacing-600);
    align-self: stretch;
    display: flex;

    & + & {
      border-left: 1px solid var(--cds-color-grey-50);
    }

    .cds-ContextualHelp-container {
      /* keeps subtitle CTAs on the same line for desktop */
      display: flex !important;
    }
  `,
  itemWrapper: css`
    align-self: center;
    margin: 0 auto;
  `,
  mobileItem: () => css`
    margin: var(--cds-spacing-200) 0;
  `,
  mobileHorizontalDivider: () => css`
    width: 100%;
    left: 0;
    position: absolute;
    margin: var(--cds-spacing-300) 0;
    height: 2px;
  `,
};

type Props = {
  keyInformationItems: React.ReactNode[];
};

const KeyInformationHorizontalModule = ({ keyInformationItems }: Props) => {
  const userAgent = useUserAgent();
  const theme = useTheme();

  const keyInfoItems = keyInformationItems.filter((item) => {
    return item !== false;
  });

  const renderDesktopInfoItems = () => {
    const itemWidth =
      keyInformationItems.length < MAX_NUMBER_OF_KIM_ITEMS ? `${Math.floor(100 / keyInfoItems.length)}%` : undefined;
    return keyInfoItems.map((keyInfoItem, index) => (
      <div key={`key-info-${index}`} css={styles.desktopItem(theme, itemWidth)}>
        <div css={styles.itemWrapper}>{keyInfoItem}</div>
      </div>
    ));
  };

  const renderMobileInfoItems = () => {
    return keyInfoItems.map((keyInfoItem, index) => (
      <div key={`key-info-${index}`} css={styles.mobileItem}>
        {keyInfoItem}
        {index === keyInfoItems.length - 1 && <Divider orientation="horizontal" css={styles.mobileHorizontalDivider} />}
      </div>
    ));
  };

  return (
    <section css={styles.wrapper}>
      <div css={styles.items}>
        <Media query={{ maxWidth: theme.breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
          {(matches) => (matches ? renderMobileInfoItems() : renderDesktopInfoItems())}
        </Media>
      </div>
    </section>
  );
};

export default KeyInformationHorizontalModule;
